import type { MenuSection } from '@1js/acui-menu/lib/components/MenuDefinition';
import type { RibbonButtonProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon';
import { constructKeytip, getKeytipString } from 'owa-command-ribbon';
import { getComposeRibbonId } from 'owa-compose-ribbon-utils/lib/utils/getComposeRibbonId';
import setImportance from 'owa-mail-compose-actions/lib/actions/setImportance';
import { getComposeViewState } from 'owa-mail-reading-pane-store/lib/utils/getComposeViewState';
import type { ComposeViewState } from 'owa-mail-compose-store';
import { ControlInfo, getControlInfo } from 'owa-mail-ribbon-utils';
import { MailRibbonControlId } from 'owa-ribbon-ids';
import type ImportanceType from 'owa-service/lib/contract/ImportanceType';
import { createMailAppSelectionButton } from '../../util/createMailRibbonButton';

/**
 * This function returns the drop-down content of the "Set Importance" button in the Toolbar Ribbon.
 * Note that SLR and MLR just have a low/high importance button. The toolbar version is three buttons,
 * High, Normal, and Low
 */
export default function getImportanceSections(
    popoutComposeViewState?: ComposeViewState
): MenuSection[] {
    return [{ controls: getimportanceButtons(popoutComposeViewState) }];
}

function getimportanceButtons(popoutComposeViewState?: ComposeViewState): RibbonButtonProps[] {
    const viewState: ComposeViewState = popoutComposeViewState ?? getComposeViewState()!;
    const highImportanceControlInfo: ControlInfo = getControlInfo(
        MailRibbonControlId.ToolbarHighImportance
    );
    const lowImportanceControlInfo: ControlInfo = getControlInfo(
        MailRibbonControlId.ToolbarLowImportance
    );
    const normalImportanceControlInfo: ControlInfo = getControlInfo(
        MailRibbonControlId.ToolbarNormalImportance
    );

    return [
        createMailAppSelectionButton(
            getComposeRibbonId(MailRibbonControlId.HighImportance, popoutComposeViewState),
            highImportanceControlInfo.text,
            highImportanceControlInfo.tooltip,
            () => {
                const viewState: ComposeViewState =
                    popoutComposeViewState ?? getComposeViewState()!;
                changeImportance(viewState, 'High');
            },
            viewState.importance === 'High',
            constructKeytip(
                [getKeytipString(MailRibbonControlId.SetImportance)],
                getKeytipString(MailRibbonControlId.HighImportance)
            )
        ),
        createMailAppSelectionButton(
            getComposeRibbonId(MailRibbonControlId.NormalImportance, popoutComposeViewState),
            normalImportanceControlInfo.text,
            normalImportanceControlInfo.tooltip,
            () => {
                const viewState: ComposeViewState =
                    popoutComposeViewState ?? getComposeViewState()!;
                changeImportance(viewState, 'Normal');
            },
            viewState.importance === 'Normal',
            constructKeytip(
                [getKeytipString(MailRibbonControlId.SetImportance)],
                getKeytipString(MailRibbonControlId.NormalImportance)
            )
        ),
        createMailAppSelectionButton(
            getComposeRibbonId(MailRibbonControlId.LowImportance, popoutComposeViewState),
            lowImportanceControlInfo.text,
            lowImportanceControlInfo.tooltip,
            () => {
                const viewState: ComposeViewState =
                    popoutComposeViewState ?? getComposeViewState()!;
                changeImportance(viewState, 'Low');
            },
            viewState.importance === 'Low',
            constructKeytip(
                [getKeytipString(MailRibbonControlId.SetImportance)],
                getKeytipString(MailRibbonControlId.LowImportance)
            )
        ),
    ];
}

function changeImportance(viewState: ComposeViewState, importance: ImportanceType) {
    if (viewState.importance !== importance) {
        setImportance(viewState, importance);
    }
}
